import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Mail } from 'lucide-react';

export default function ForgotPassword() {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPending(true);
    setError('');

    try {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to send reset email');
        setPending(false);
        return;
      }

      setSuccess(true);
    } catch (err) {
      setError('An error occurred. Please try again.');
      setPending(false);
    }
  };

  if (success) {
    return (
      <div className="h-screen flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 bg-background">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center mb-6">
            <div className="rounded-full bg-success-surface-strong p-3">
              <Mail className="h-8 w-8 text-success-foreground" />
            </div>
          </div>
          <h2 className="text-center text-3xl font-medium text-content-heading">
            Check your email
          </h2>
          <p className="mt-4 text-center text-content-secondary">
            If an account exists for <span className="font-medium">{email}</span>, 
            we've sent a password reset link. The link will expire in 1 hour.
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-xs">
          <Link
            to="/sign-in"
            className="flex items-center justify-center gap-2 text-sm font-medium text-primary hover:text-primary/80"
          >
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 bg-background">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-medium text-content-heading">
          Reset your password
        </h2>
        <p className="mt-2 text-center text-sm text-content-secondary">
          Enter your email address and we'll send <br /> you a link to reset your password.
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-xs">
        <form className="space-y-6" onSubmit={handleSubmit}>
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            maxLength={255}
            inlineLabel="Email"
            placeholder="Enter your email"
            className="w-full"
          />
          {error && (
            <div className="text-danger-text text-sm -mt-4">{error}</div>
          )}

          <div>
            <Button
              type="submit"
              disabled={pending}
              className="w-full"
              variant="primary"
            >
              {pending ? (
                <>
                  <Loader2 className="animate-spin mr-2 h-4 w-4" />
                  Sending...
                </>
              ) : (
                'Send reset link'
              )}
            </Button>
          </div>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/sign-in"
            className="flex items-center justify-center gap-2 text-sm font-medium text-primary hover:text-primary/80"
          >
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}

